/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import {
	CosmosCard,
	LicenseCardButtons,
	LicenseCardCore,
	LicenseCardSeats,
	LicenseCardTop,
} from "@components/elements";
import { cx } from "@panda/css";
import { VStack } from "@panda/jsx";

import {
	ChangeEvent,
	ChangeEventHandler,
	FocusEvent,
	FocusEventHandler,
	MouseEventHandler,
	useCallback,
	useState,
} from "react";
import { CosmosElementIconButton, CosmosEnumCountryCodes, LicenseMainCardFragment } from "@cosmos/hygraph-schema";
import { usePrice } from "@cosmos/next-hooks";
import { DEFAULT_UE_SEAT_PRICE_NUM, DEFAULT_UE_SEAT_PRICE_STR } from "@components/constants";

export interface LicenseSeatData {
	defaultPriceNum?: number;
	defaultPriceStr?: string;
	seatOfferId?: string;
	seatNamespace?: string;
	seatPurchaseUrl?: string; // This should be the seat purchase url with params expecting seatCount to be appended, like `...quanity=${seatCount}
}

type LicenseMainCardWithSeatData = LicenseMainCardFragment & LicenseSeatData;

export interface LicenseMainCardProps extends LicenseMainCardWithSeatData {
	locale: string;
	country: CosmosEnumCountryCodes;
	className?: string;
	showExampleLabel?: string | null;
	hideExampleLabel?: string | null;
}

export const LicenseMainCard = ({
	locale,
	country,
	className = "",
	cardTop,
	cardSections = [],
	seats,
	showExampleLabel,
	hideExampleLabel,
	defaultPriceNum,
	defaultPriceStr,
	seatOfferId,
	seatNamespace,
	seatPurchaseUrl,
}: LicenseMainCardProps): React.JSX.Element => {
	const { price } = usePrice(locale, seatOfferId || "", seatNamespace || "", country);
	const divideBy = Math.pow(10, price?.currencyInfo?.decimals || 0);
	const priceNum = (price?.discountPrice || defaultPriceNum || DEFAULT_UE_SEAT_PRICE_NUM) / divideBy;
	const priceStr = (price?.discountPriceStr || defaultPriceStr || DEFAULT_UE_SEAT_PRICE_STR).replace(/\.00$/, "");

	const [seatCount, setSeatCount] = useState(1);
	const handleDecrementClick: MouseEventHandler<HTMLButtonElement> = useCallback(() => {
		const newSeatCount = Math.max(seatCount - 1, 1);
		setSeatCount(newSeatCount);
	}, [seatCount]);
	const handleIncrementClick: MouseEventHandler<HTMLButtonElement> = useCallback(() => {
		const newSeatCount = Math.min(seatCount + 1, 25);
		setSeatCount(newSeatCount);
	}, [seatCount]);
	const handleInputChange: ChangeEventHandler<HTMLInputElement> = useCallback((e: ChangeEvent<HTMLInputElement>) => {
		const inputValue = parseInt(e?.target?.value, 10);
		setSeatCount(Math.min(Math.max(inputValue, 1), 25));
	}, []);
	const handleInputBlur: FocusEventHandler<HTMLInputElement> = useCallback((e: FocusEvent<HTMLInputElement>) => {
		const inputValue = parseInt(e?.target?.value, 10);
		if (isNaN(inputValue)) setSeatCount(1);
	}, []);

	return (
		<CosmosCard className={cx("license-main-card", className)} padding={32} variant="material">
			<VStack alignItems="flex-start" justifyContent="space-between" gap="eds.dimension.32" h="full">
				<VStack alignItems="flex-start" gap="eds.dimension.32">
					<LicenseCardTop
						{...(cardTop || {})}
						showExampleLabel={showExampleLabel}
						hideExampleLabel={hideExampleLabel}
					/>
					{(cardSections || []).map((section) => (
						<LicenseCardCore
							key={`card-section-${section.title}`}
							priceStr={priceStr}
							{...(section?.countriesToOverride?.includes(country)
								? { ...section, ...section?.countryOverride }
								: section)}
						/>
					))}
				</VStack>
				{seats && !seats?.countriesToOverride?.includes(country) ? (
					<LicenseCardSeats
						locale={locale}
						{...seats}
						buttons={seats?.buttons as CosmosElementIconButton[]}
						countriesToOverride={seats?.countriesToOverride || []}
						seatCount={seatCount}
						country={country}
						currencyCode={price?.currencyCode}
						priceNum={priceNum}
						priceStr={priceStr}
						onDecrementClick={handleDecrementClick}
						onIncrementClick={handleIncrementClick}
						onInputChange={handleInputChange}
						onInputBlur={handleInputBlur}
					/>
				) : null}
				{seats ? (
					<LicenseCardButtons
						buttons={
							seats?.countriesToOverride?.includes(country)
								? seats?.countryOverride?.buttons || []
								: seats?.buttons || []
						}
						seatCount={seats?.countriesToOverride?.includes(country) ? 0 : seatCount}
						seatPurchaseUrl={seatPurchaseUrl}
					/>
				) : (
					<LicenseCardButtons buttons={cardSections[cardSections.length - 1]?.buttons || []} />
				)}
			</VStack>
		</CosmosCard>
	);
};
